// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { Dispatch } from "redux";
import { ActionTypes } from "./actionTypes";
import { createPayloadAction, IPayloadAction } from "./actionCreators";
import { IAppSettings, ILoadProjectTokensCommand, ISecurityToken, IValidateUserKeyCommand } from "../../models/applicationState";
import { IProject, IApplicationState } from "../../models/applicationState";
import { generateKey } from "../../common/crypto";
import { getCookieEmail, parseEndpointByType } from "../../react/components/common/jsonSchemaFormHelper";
import axios from "axios";

/**
 * Actions to make changes to application settings
 * @member toggleDevTools - Open or close dev tools
 * @member reloadApplication - Reload application
 */
export default interface IApplicationActions {
    loadAppSettings(): IAppSettings;
    saveAppSettings(appSettings: IAppSettings): IAppSettings;
    ensureSecurityToken(project: IProject, callback?: (token: ISecurityToken) => Promise<void>): IAppSettings;
    validateAppByUserKey(userKey: string): any;
    setWidgetMode(): void;
    setUploadDocumentSuccessFlag(): void;
    logout(): void;
}

export function logout() {
    return new Promise((resolve, reject) => {
        axios
            .post(parseEndpointByType("Logout"))
            .then((response: any) => { 
                resolve(null);
            })
            .catch((error) => reject(error))
            .finally();
    });
}

/**
 * Save app settings
 */
export function saveAppSettings(appSettings: IAppSettings): (dispatch: Dispatch) => Promise<IAppSettings> {
    return (dispatch: Dispatch) => {
        dispatch(saveAppSettingsAction(appSettings));
        return Promise.resolve(appSettings);
    };
}

export function loadAppSettings(): (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAppSettings> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const response = await (getAppSettings()) as any
        if (response.successful && response.stringValue1 && response.stringValue1 !== '') {
            const appSettings = JSON.parse(response.stringValue1)
            dispatch(loadAppSettingsAction(appSettings));
            return appSettings
        }
    };
}

function getAppSettings() {
    return new Promise((resolve, reject) => {
        const email = getCookieEmail();
        if (email) {
            axios
                .post(parseEndpointByType("LoadProjectTokens"), {
                    email: email,
                } as ILoadProjectTokensCommand)
                .then((response: any) => { 
                    if (response && response.data) {
                        resolve(response.data);
                    }
                    else {
                        resolve([]);
                    }                    
                })
                .catch((error) => reject(error))
                .finally();
        } else {
            resolve([]);
        }
    });
}

export function validateAppByUserKey(userKey: string): (dispatch: Dispatch) => Promise<any> {
    return async (dispatch: Dispatch) => {
        const response = (await validateUserKey(userKey)) as any;
        return response;
    }
}

export function setWidgetMode() {
    return async (dispatch: Dispatch) => {
        dispatch(appOnWidgetModeAction(true));
    }
}

export function setUploadDocumentSuccessFlag() {
    return async (dispatch: Dispatch) => {
        dispatch(appOnUploadDocumentSuccess(new Date().getTime()));
    }
}

function validateUserKey(userKey: string) {
    return new Promise((resolve, reject) => {
        axios
            .post(parseEndpointByType("ValidateUserKey"), {
                userKey
            } as IValidateUserKeyCommand)
            .then((response: any) => resolve(response.data))
            .catch((error) => reject(error))
            .finally();
    });
}

/**
 * Ensures that a valid security token is associated with the project, otherwise creates one
 * @param project The project to validate
 */
export function ensureSecurityToken(project: IProject, callback?: (token: ISecurityToken) => Promise<void>):
    (dispatch: Dispatch, getState: () => IApplicationState) => Promise<IAppSettings> {
    return async (dispatch: Dispatch, getState: () => IApplicationState) => {
        const appState = getState();

        // check if there's token with the same prefix
        const tokenPrefix = `${project.name} Token`;
        let tokenCountWithTheSamePrefix = 0
        let securityToken = {} as ISecurityToken

        if (appState.appSettings.securityTokens) {
            let securityToken = appState.appSettings.securityTokens
                .find((st) => st.name === project.securityToken);

            tokenCountWithTheSamePrefix = appState.appSettings.securityTokens
                .filter((st) => st.name.startsWith(tokenPrefix))
                .length;

            if (securityToken) {
                return appState.appSettings;
            }
        }

        const tokenSuffix = tokenCountWithTheSamePrefix === 0 ? `` : ` [${tokenCountWithTheSamePrefix}]`;
        securityToken = {
            name: `${tokenPrefix}${tokenSuffix}`,
            key: generateKey(),
        };

        const updatedAppSettings: IAppSettings = {
            securityTokens: appState.appSettings.securityTokens ? [...appState.appSettings.securityTokens, securityToken] : [securityToken]
        };

        await this.saveAppSettings(updatedAppSettings);

        project.securityToken = securityToken.name;
        dispatch(ensureSecurityTokenAction(updatedAppSettings));
        if (callback) {
            callback(securityToken);
        }
        return updatedAppSettings;
    };
}

/**
 * Save app settings action type
 */
export interface ISaveAppSettingsAction extends IPayloadAction<string, IAppSettings> {
    type: ActionTypes.SAVE_APP_SETTINGS_SUCCESS;
}

/**
 * Save app settings action type
 */
export interface ILoadAppSettingsAction extends IPayloadAction<string, IAppSettings> {
    type: ActionTypes.LOAD_APP_SETTINGS_SUCCESS;
}

export interface IAppOnWidgetModeAction extends IPayloadAction<string, boolean> {
    type: ActionTypes.APP_ON_WIDGET_MODE;
}

export interface IAppOnUploadDocumentSuccessAction extends IPayloadAction<string, number> {
    type: ActionTypes.UPLOAD_DOCUMENT_SUCCESS_FLAG;
}

/**
 * Ensure project security token action type
 */
export interface IEnsureSecurityTokenAction extends IPayloadAction<string, IAppSettings> {
    type: ActionTypes.ENSURE_SECURITY_TOKEN_SUCCESS;
}

/**
 * Instance of save app settings action
 */
export const saveAppSettingsAction = createPayloadAction<ISaveAppSettingsAction>(ActionTypes.SAVE_APP_SETTINGS_SUCCESS);

/**
 * Instance of Export Project action
 */
export const ensureSecurityTokenAction =
    createPayloadAction<IEnsureSecurityTokenAction>(ActionTypes.ENSURE_SECURITY_TOKEN_SUCCESS);

/**
* Instance of save app settings action
*/
export const loadAppSettingsAction = createPayloadAction<ILoadAppSettingsAction>(ActionTypes.LOAD_APP_SETTINGS_SUCCESS);

export const appOnWidgetModeAction = createPayloadAction<IAppOnWidgetModeAction>(ActionTypes.APP_ON_WIDGET_MODE);

export const appOnUploadDocumentSuccess = createPayloadAction<IAppOnUploadDocumentSuccessAction>(ActionTypes.UPLOAD_DOCUMENT_SUCCESS_FLAG);

